import { useLazyQuery } from "@apollo/client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { GetCaseTypes } from "../queries/query";
import {
  CommonChooseProps,
  GetCaseTypeData,
  GetCaseTypeVars,
} from "../queries/types";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import useToken from "../../../../../customHooks/useToken";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { EMPTY_STRING } from "../../../../../utils/constants";
import { Keys } from "../../../../../utils/Enum.keys";
import { handleFormEvent } from "../../../../../utils/UtilFunctions";
import ClearIcon from '@mui/icons-material/Clear';

export type onChangeCaseStudyType = (e: string, selected: boolean) => void;
interface ChooseCaseTypeProps extends CommonChooseProps {
  onChange?: onChangeCaseStudyType;
}

const CaseTypeAutocomplete = ({
  error,
  disabled,
  type,
  required,
  value,
  onChange,
  autoFocus,
  openOnFocus,
  clearFunction,
  onKeyDown,
  selectedId,
  label,
  ...props
}: ChooseCaseTypeProps) => {
  const { customer_id } = useUrlParams();
  const { handleToken } = useToken();
  const inputRef = useRef(null);
  const nameTextField = props.name;
  const [showOptions, setShowOptions] = useState(false);
  const [caseType, setCaseType] = useState<string[]>([]);
  const [hover, setHover] = useState(false);

  const [i, setI] = useState(-1);

  const [GetCaseTypeDetails, { data, loading, fetchMore }] = useLazyQuery<
    GetCaseTypeData,
    GetCaseTypeVars
  >(GetCaseTypes);
  useEffect(() => {
    const fetchCaseTypes = async () => {
      try {
        const token = await handleToken();
        if (token && customer_id) {
          GetCaseTypeDetails({
            variables: {
              customerID: customer_id,
              token,
            },
          }).then((res) => {
            if (res.data) {
              const newData = res.data.GetCaseTypes;
              setCaseType(newData);
            }
          });
        }
      } catch (error) {
        console.error((error as Error).message);
      }
    };

    fetchCaseTypes();
  }, [customer_id, GetCaseTypeDetails]);

  useEffect(() => {
    document.addEventListener("click", () => {
      if ((document.activeElement as HTMLInputElement).name !== nameTextField) {
        setShowOptions(false);
      }
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === Keys.Esc) {
        setShowOptions(false);
      }
    });
    return () => {
      document.removeEventListener("click", () => {
        if ((document.activeElement as HTMLInputElement).name !== nameTextField)
          setShowOptions(false);
      });
      document.addEventListener("keydown", (e) => {
        if (e.key === Keys.Esc) {
          setShowOptions(false);
        }
      });
    };
  }, []);

  const filteredCompanies = useMemo(() => {
    if (value)
      return caseType.filter(
        (res) =>
          res.toLocaleLowerCase().includes(value.toLocaleLowerCase()) ||
          value === EMPTY_STRING
      );
    else return caseType;
  }, [value, caseType]);

  return (
    <>
      <div
        className="predefined-select"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <TextField
          className="predefined-select__textfield"
          required={required}
          value={value}
          type={type}
          ref={inputRef}
          autoFocus={autoFocus}
          {...props}
          autoComplete="off"
          label={label}
          onFocus={() => {
            setShowOptions(true);
          }}
          onChange={(e) => {
            if (onChange) {
              onChange(e.target.value, false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === Keys.ArrowDown) {
              if (caseType.length > i + 1) {
                setI(i + 1);
                const selectedItem =
                  document.getElementsByClassName("custom-list-item");
                if (selectedItem.length) {
                  selectedItem[i + 1].scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }
              }
            }
            if (e.key === Keys.ArrowUp) {
              if (i !== -1) setI(i - 1);
              const selectedItem =
                document.getElementsByClassName("custom-list-item");
              if (selectedItem[i - 1])
                if (selectedItem.length) {
                  selectedItem[i - 1].scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }
            }
            if (e.key === Keys.ENTER) {
              e.preventDefault();
              if (caseType[i]) {
                if (onChange) onChange(caseType[i], true);

                handleFormEvent(e);
              } else {
                handleFormEvent(e);
              }

              if (onKeyDown) onKeyDown(e);
              setShowOptions(false);
            }
          }}
          slotProps={{
            input: {
              endAdornment: value && hover && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={clearFunction}
                    edge="end"
                    sx={{
                      opacity: 0.6,
                      transition: "opacity 0.2s",
                      "&:hover": {
                        opacity: 1,
                        color: "primary.main",
                      },
                      marginRight: "2px",
                    }}
                  >
                    <ClearIcon
                      sx={{
                        fontSize: 15,
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            },
            inputLabel: { shrink: true },
          }}
          disabled={disabled}
        />
        {showOptions ? (
          <ul className="list-tag" tabIndex={0} id="ul">
            {filteredCompanies.map((result, index) => {
              return (
                <li
                  key={result}
                  tabIndex={0}
                  className={`custom-list-item ${
                    i === index ? "selected-item" : ""
                  }`}
                  onClick={() => {
                    if (onChange) {
                      onChange(result, true);
                    }
                  }}
                >
                  {result}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </>
  );
};

export default CaseTypeAutocomplete;
